'use client';
import cn from 'classnames';
import { CSSProperties, useEffect, useState } from 'react';
import styles from './showmore.module.scss';
import { Button } from '@/ui-components/atoms/Button/Button';
import { IconDropDown } from '@/assets/icons/IconDropdown';

export interface ShowMoreProps {
    showText?: React.ReactNode;
    hideText?: React.ReactNode;
    color?: string;
    className?: string;
    buttonClassName?: string;
    arrowClassName?: string;
    activeToggleTextPosition?: 'top' | 'bottom';
    ariaLabel?: string;
    id: string;
    contentClassName?: string;
    shownClassName?: string;
    children?: React.ReactNode;
    onClickHandler?: () => void;
    isContentShown?: boolean;
    ariaHaspopup?: boolean;
    showMoreAreaStyles?: string;
    TagName?: 'div' | 'span';
}

export function ShowMore({
    showText = 'Show',
    hideText = 'Hide',
    color,
    className,
    buttonClassName,
    arrowClassName,
    activeToggleTextPosition,
    ariaLabel = 'Show more',
    id = '',
    contentClassName = '',
    shownClassName = '',
    showMoreAreaStyles = '',
    children,
    onClickHandler,
    isContentShown = false,
    ariaHaspopup = false,
    TagName = 'div',
}: ShowMoreProps): React.JSX.Element {
    const contentId = id;
    const [contentShown, setContentShown] = useState(false);

    useEffect(() => {
        setContentShown(isContentShown);
    }, [isContentShown]);

    const handleClick = () => {
        if (onClickHandler) {
            onClickHandler();
        } else {
            setContentShown(!contentShown);
        }
    };

    const classNames = cn(styles.wrapper, className, {
        [styles.contentShown]: contentShown,
        [styles.textTop]: activeToggleTextPosition === 'top',
    });

    const contentClasses = cn(styles.contentWrapper, contentClassName, {
        [shownClassName]: contentShown,
    });

    const colorStyle = {
        ...(color ? { '--text-button-color': color } : {}),
    } as CSSProperties;

    return (
        <TagName className={classNames} style={colorStyle} data-cy="atoms-more-info-wrapper">
            <Button
                onClick={() => handleClick()}
                className={cn(styles.caret, styles.contentShowText, buttonClassName)}
                variant="tertiary"
                tertiaryNoPadding
                {...(contentShown ? { ariaExpanded: true } : {})}
                ariaControls={contentId}
                ariaLabel={ariaLabel}
                ariaHaspopup={ariaHaspopup}
                fitting="hug-contents"
                dataCy="atoms-show-more-button-2-0"
                type="button"
            >
                <ShowMoreText
                    contentShown={contentShown}
                    hideText={hideText}
                    showText={showText}
                    color={color}
                    className={showMoreAreaStyles}
                    arrowClassName={arrowClassName}
                    TagName={TagName}
                />
            </Button>
            <TagName id={contentId} className={contentClasses} data-cy="atoms-show-more-content">
                {children}
            </TagName>
        </TagName>
    );
}

function ShowMoreText({
    contentShown,
    hideText,
    showText,
    color,
    className,
    arrowClassName,
    TagName = 'div',
}: {
    contentShown: boolean;
    hideText: ShowMoreProps['hideText'];
    showText: ShowMoreProps['showText'];
    color?: string;
    className?: string;
    arrowClassName?: string;
    TagName?: 'div' | 'span';
}) {
    const colorStyle = {
        ...(color ? { '--text-button-color': color } : {}),
    } as CSSProperties;

    return (
        <TagName className={cn(styles.contentShowWrapper, className)}>
            {contentShown ? hideText : showText}
            <TagName className={cn(styles.dropdownIcon, arrowClassName)} style={colorStyle}>
                <IconDropDown />
            </TagName>
        </TagName>
    );
}
